import { type PathKey } from '~/apps/ninesegsAnalyzer/routes/paths'

export const pathKey = 'documentQa' satisfies PathKey

export type DocumentQaFilterState = {
  brandGroupId: string | null
  brandId: string | null
  surveyId: string | null
}

export const urlParamKeys = {
  brandGroupId: 'brandGroupId',
  brandId: 'brandId',
  question: 'q',
  requestId: 'requestId',
  surveyId: 'surveyId',
} as const

/**
 * Checks whether all filter fields required to issue a document QA query are
 * populated.
 *
 * @param filters - Current filter state.
 * @returns true when surveyId, brandGroupId, and brandId are all set.
 */
export function isFilterComplete(filters: DocumentQaFilterState): boolean {
  return (
    filters.surveyId !== null &&
    filters.brandGroupId !== null &&
    filters.brandId !== null
  )
}

/**
 * Serializes the question, filters, and request id into URL search params for
 * the document QA page so navigation state can be restored from the URL.
 *
 * @param question - Question text typed by the user.
 * @param filters - Filter state to encode.
 * @param requestId - Active request id, or null when no query is in flight.
 * @returns URLSearchParams populated with the non-null values.
 */
export function stateToSearchParams(
  question: string,
  filters: DocumentQaFilterState,
  requestId: string | null,
): URLSearchParams {
  const params = new URLSearchParams()

  if (question) {
    params.set(urlParamKeys.question, question)
  }
  if (filters.brandGroupId) {
    params.set(urlParamKeys.brandGroupId, filters.brandGroupId)
  }
  if (filters.brandId) {
    params.set(urlParamKeys.brandId, filters.brandId)
  }
  if (filters.surveyId) {
    params.set(urlParamKeys.surveyId, filters.surveyId)
  }
  if (requestId) {
    params.set(urlParamKeys.requestId, requestId)
  }

  return params
}

/**
 * Parses URL search params back into the document QA page's view state
 * (question text, filters, and active request id).
 *
 * @param params - URLSearchParams to read from.
 * @returns Parsed state with filters, question text, and requestId.
 */
export function searchParamsToState(params: URLSearchParams): {
  filters: DocumentQaFilterState
  question: string
  requestId: string | null
} {
  return {
    filters: {
      brandGroupId: params.get(urlParamKeys.brandGroupId) || null,
      brandId: params.get(urlParamKeys.brandId) || null,
      surveyId: params.get(urlParamKeys.surveyId) || null,
    },
    question: params.get(urlParamKeys.question) ?? '',
    requestId: params.get(urlParamKeys.requestId) || null,
  }
}
